import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/currency";
import { formatDistanceToNow } from "date-fns";
import {
  ShoppingBag, Plus, Search, MapPin, MessageSquare, Trash2, Tag, X, ImagePlus, CheckCircle2, Package, ShieldAlert, Flag,
} from "lucide-react";

export default function Classifieds() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [listings, setListings] = useState([]);
  const [meta, setMeta] = useState({ categories: [], conditions: [] });
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [includeSold, setIncludeSold] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyDraft = { title: "", description: "", price: 0, is_free: false, condition: "Good", category: "Other", images: [], city: "", state: "", shipping_available: false };
  const [draft, setDraft] = useState(emptyDraft);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // listing being reported
  const [reportReason, setReportReason] = useState("spam");
  const [reportDetails, setReportDetails] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportReasons, setReportReasons] = useState([]);

  const load = () => {
    const params = { include_sold: includeSold };
    if (category !== "all") params.category = category;
    if (search) params.search = search;
    api.get("/classifieds", { params }).then((r) => setListings(r.data));
  };

  useEffect(() => {
    api.get("/classifieds/meta/options").then((r) => setMeta(r.data));
    api.get("/report-reasons").then((r) => setReportReasons(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, category, includeSold]);

  const openNew = () => {
    if (!user || user === false) { nav("/login"); return; }
    setEditingId(null);
    setDraft({ ...emptyDraft, city: user.city || "", state: user.state || "" });
    setShowForm(true);
  };
  const openEdit = (listing) => {
    setEditingId(listing.id);
    setDraft({
      title: listing.title, description: listing.description, price: listing.price, is_free: listing.is_free,
      condition: listing.condition, category: listing.category, images: listing.images || [],
      city: listing.city || "", state: listing.state || "", shipping_available: listing.shipping_available,
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); };

  const uploadImg = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (5MB max)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, images: [...d.images, data.path || data.url].slice(0, 6) }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setUploading(false);
  };

  const removeImg = (idx) => setDraft((d) => ({ ...d, images: d.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (draft.title.trim().length < 3 || draft.description.trim().length < 10) { toast.error("Add a title (3+ chars) and description (10+ chars)"); return; }
    setBusy(true);
    try {
      const payload = { ...draft, price: draft.is_free ? 0 : (parseFloat(draft.price) || 0) };
      if (editingId) await api.patch(`/classifieds/${editingId}`, payload);
      else await api.post("/classifieds", payload);
      toast.success(editingId ? "Listing updated" : "Listing posted");
      closeForm();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const toggleSold = async (id) => {
    try { await api.post(`/classifieds/${id}/toggle-sold`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/classifieds/${id}`); load();
  };

  const openReport = (listing) => {
    if (!user || user === false) { nav("/login"); return; }
    setReportTarget(listing);
    setReportReason(reportReasons[0]?.key || "spam");
    setReportDetails("");
  };
  const submitReport = async () => {
    if (!reportTarget) return;
    setReportBusy(true);
    try {
      await api.post(`/classifieds/${reportTarget.id}/report`, { reason: reportReason, details: reportDetails.trim() || null });
      toast.success("Report submitted — moderators will review it.");
      setReportTarget(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setReportBusy(false);
  };

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <Link to="/marketplace" className="hover:text-[#FF7E67]">Marketplace</Link>
              <span>/</span>
              <span>Classifieds</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              Baker's <span style={{ color: "#8EAC50" }}>Classifieds</span>
            </h1>
            <p className="mt-2 text-[#857163] max-w-2xl">Buy or sell used equipment, tools, packaging, and bulk ingredients from fellow bakers in the community.</p>
          </div>
          <button onClick={openNew} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white font-semibold text-sm hover:opacity-95 transition shrink-0" style={{ background: "#8EAC50" }} data-testid="post-listing-btn"><Plus className="w-4 h-4" /> Post a listing</button>
        </div>

        <div className="mb-4 rounded-2xl p-3 sm:p-4 flex items-start gap-3" style={{ background: "rgba(194,24,91,0.08)", border: "1px solid rgba(194,24,91,0.15)" }} data-testid="safety-banner">
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#C2185B" }} />
          <div className="text-xs sm:text-sm text-[#3B2C24] leading-snug">
            <span className="font-semibold">Stay safe:</span> meet in a public place, inspect items before paying, and never wire money in advance. Cake Pop Talk isn't a party to these transactions. See a suspicious listing? Use <Flag className="w-3 h-3 inline align-[-1px]" /> Report.
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 items-center">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 max-w-md relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857163]" />
            <input className="cp-input !pl-11" placeholder="Search listings…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" />
          </form>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="cp-input !py-2 !text-sm max-w-[180px]" data-testid="cat-select">
            <option value="all">All categories</option>
            {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm text-[#3B2C24] cursor-pointer">
            <input type="checkbox" checked={includeSold} onChange={(e) => setIncludeSold(e.target.checked)} className="w-4 h-4" data-testid="include-sold-toggle" />
            <span>Show sold</span>
          </label>
        </div>

        {listings.length === 0 ? (
          <div className="cp-card p-10 text-center">
            <Package className="w-12 h-12 mx-auto text-[#857163] mb-2" />
            <div className="font-heading text-2xl text-[#3B2C24]">No listings yet</div>
            <p className="text-[#857163] mt-1">Be the first to post used equipment or supplies.</p>
            <button onClick={openNew} className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white font-semibold text-sm" style={{ background: "#8EAC50" }}><Plus className="w-4 h-4" /> Post a listing</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="listings-grid">
            {listings.map((l) => {
              const isMine = user && user !== false && l.seller?.user_id === user.id;
              const isAdmin = user?.role === "admin";
              return (
                <div key={l.id} className="cp-card overflow-hidden flex flex-col relative" data-testid={`listing-${l.id}`}>
                  {l.is_sold && <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-xs font-bold text-white" style={{ background: "#3B2C24" }}>SOLD</div>}
                  <div className="h-40 bg-[#F9EFE6] relative">
                    {l.images?.[0] ? (
                      <img src={fileUrl(l.images[0])} alt="" className={`w-full h-full object-cover ${l.is_sold ? "opacity-50" : ""}`} />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-[#857163]"><ImagePlus className="w-8 h-8 opacity-40" /></div>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="cp-tag text-[10px]" style={{ background: "rgba(142,172,80,0.15)", color: "#5F7A2E" }}>{l.category}</span>
                      <span className="cp-tag text-[10px]" style={{ background: "rgba(133,113,99,0.15)", color: "#857163" }}>{l.condition}</span>
                      {l.shipping_available && <span className="cp-tag text-[10px]" style={{ background: "rgba(47,128,237,0.15)", color: "#2F80ED" }}>Ships</span>}
                    </div>
                    <h3 className="font-heading text-lg text-[#3B2C24] mt-1.5 leading-tight line-clamp-2">{l.title}</h3>
                    <div className="mt-1 font-heading text-xl font-bold" style={{ color: "#8EAC50" }}>
                      {l.is_free ? "Free" : fmtMoney(l.price, l.seller?.currency)}
                    </div>
                    <div className="text-xs text-[#857163] mt-1 flex items-center gap-2 flex-wrap">
                      {(l.city || l.state) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[l.city, l.state].filter(Boolean).join(", ")}</span>}
                      <span>{(() => { try { return formatDistanceToNow(new Date(l.created_at), { addSuffix: true }); } catch { return ""; } })()}</span>
                    </div>
                    <div className="mt-auto pt-3 flex items-center justify-between gap-2 flex-wrap">
                      <Link to={`/u/${l.seller?.username}`} className="text-xs text-[#857163] hover:text-[#FF7E67] truncate">@{l.seller?.username}</Link>
                      <div className="flex gap-1">
                        {!isMine && user && user !== false && !l.is_sold && (
                          <button onClick={() => nav(`/messages/${l.seller.user_id}`)} className="p-1.5 rounded-full text-white hover:opacity-90" style={{ background: "#8EAC50" }} title="Message seller" data-testid={`msg-${l.id}`}><MessageSquare className="w-3.5 h-3.5" /></button>
                        )}
                        {!isMine && (
                          <button onClick={() => openReport(l)} className="p-1.5 rounded-full hover:bg-red-50 text-[#C2185B]" title="Report listing" data-testid={`report-${l.id}`}><Flag className="w-3.5 h-3.5" /></button>
                        )}
                        {isMine && (
                          <>
                            <button onClick={() => toggleSold(l.id)} className={`p-1.5 rounded-full ${l.is_sold ? "bg-[#857163]/20 text-[#3B2C24]" : "bg-[#4C9F70]/20 text-[#4C9F70]"} hover:bg-opacity-80`} title={l.is_sold ? "Mark active" : "Mark sold"} data-testid={`sold-${l.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => openEdit(l)} className="p-1.5 rounded-full hover:bg-[#F9EFE6]" title="Edit" data-testid={`edit-${l.id}`}><Tag className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                        {(isMine || isAdmin) && (
                          <button onClick={() => del(l.id)} className="p-1.5 rounded-full text-red-500 hover:bg-red-50" title="Delete" data-testid={`del-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Post/Edit Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeForm} data-testid="listing-modal">
            <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 sm:p-6 space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-heading text-2xl text-[#3B2C24]">{editingId ? "Edit listing" : "Post a listing"}</h2>
                  <button onClick={closeForm} className="p-1.5 hover:bg-[#F9EFE6] rounded-full"><X className="w-4 h-4" /></button>
                </div>
                <label><span className="text-xs text-[#857163]">Title</span><input className="cp-input mt-1" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="KitchenAid Pro 6-quart mixer" data-testid="draft-title" /></label>
                <label><span className="text-xs text-[#857163]">Description</span><textarea className="cp-input mt-1 min-h-[120px] resize-y text-sm" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Condition, dimensions, why you're selling, pickup vs. shipping…" data-testid="draft-description" /></label>
                <div className="grid grid-cols-2 gap-3">
                  <label><span className="text-xs text-[#857163]">Price</span><input type="number" step={0.01} min={0} className="cp-input mt-1" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} disabled={draft.is_free} data-testid="draft-price" /></label>
                  <label className="flex items-end gap-2 pb-2"><input type="checkbox" checked={draft.is_free} onChange={(e) => setDraft({ ...draft, is_free: e.target.checked })} className="w-4 h-4" data-testid="draft-free" /><span className="text-sm text-[#3B2C24]">Free / giveaway</span></label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label><span className="text-xs text-[#857163]">Category</span><select className="cp-input mt-1" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="draft-category">{meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
                  <label><span className="text-xs text-[#857163]">Condition</span><select className="cp-input mt-1" value={draft.condition} onChange={(e) => setDraft({ ...draft, condition: e.target.value })} data-testid="draft-condition">{meta.conditions.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label><span className="text-xs text-[#857163]">City</span><input className="cp-input mt-1" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} placeholder="Boston" data-testid="draft-city" /></label>
                  <label><span className="text-xs text-[#857163]">State / Region</span><input className="cp-input mt-1" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} placeholder="MA" data-testid="draft-state" /></label>
                </div>
                <label className="flex items-center gap-2"><input type="checkbox" checked={draft.shipping_available} onChange={(e) => setDraft({ ...draft, shipping_available: e.target.checked })} className="w-4 h-4" data-testid="draft-shipping" /><span className="text-sm text-[#3B2C24]">Shipping available (buyer pays)</span></label>

                <div>
                  <div className="text-xs text-[#857163] mb-1">Photos ({draft.images.length}/6)</div>
                  <div className="flex flex-wrap gap-2">
                    {draft.images.map((img, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#EBDCD1]">
                        <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => removeImg(i)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5" data-testid={`remove-img-${i}`}><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {draft.images.length < 6 && (
                      <label className="w-20 h-20 rounded-xl border-2 border-dashed border-[#EBDCD1] grid place-items-center cursor-pointer hover:bg-[#F9EFE6]">
                        <input type="file" accept="image/*" onChange={(e) => uploadImg(e.target.files?.[0])} className="hidden" data-testid="draft-upload" />
                        {uploading ? <span className="text-xs text-[#857163]">…</span> : <ImagePlus className="w-5 h-5 text-[#857163]" />}
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-3 border-t border-[#EBDCD1]">
                  <button onClick={closeForm} className="cp-btn-ghost">Cancel</button>
                  <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50" style={{ background: "#8EAC50" }} data-testid="save-listing">{busy ? "Saving…" : editingId ? "Update listing" : "Post listing"}</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Report Modal */}
        {reportTarget && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !reportBusy && setReportTarget(null)} data-testid="report-modal">
            <div className="bg-white rounded-3xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#C2185B]"><Flag className="w-3.5 h-3.5" /> <span>Report listing</span></div>
                  <h2 className="font-heading text-xl text-[#3B2C24] mt-1 line-clamp-2">{reportTarget.title}</h2>
                </div>
                <button onClick={() => setReportTarget(null)} className="p-1.5 hover:bg-[#F9EFE6] rounded-full"><X className="w-4 h-4" /></button>
              </div>
              <label><span className="text-xs text-[#857163]">Reason</span>
                <select className="cp-input mt-1" value={reportReason} onChange={(e) => setReportReason(e.target.value)} data-testid="report-reason">
                  {reportReasons.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </label>
              <label className="block mt-3"><span className="text-xs text-[#857163]">Details (optional)</span>
                <textarea className="cp-input mt-1 min-h-[100px] resize-y text-sm" value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} maxLength={500} placeholder="Anything moderators should know…" data-testid="report-details" />
              </label>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setReportTarget(null)} className="cp-btn-ghost" disabled={reportBusy}>Cancel</button>
                <button onClick={submitReport} disabled={reportBusy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50" style={{ background: "#C2185B" }} data-testid="submit-report">{reportBusy ? "Submitting…" : "Submit report"}</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
